// Printable (print / save-as-PDF) reports for the three assessments a product or
// train addition produces: Risk Assessment, Impact Assessment and Gap Assessment.
// Each builder returns report-body HTML; printAssessmentReport wraps it in the
// standard client letterhead from printUtils and opens the print dialog.
import { setReportSettingsCache, unitReportAddress } from './reportProfiles';
import { toast } from 'react-toastify';
import { settingsAPI } from '../services/api';
import { generatePrintHTML, getActivePrintUnitName } from './printUtils';

const esc = (value) => String(value ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const text = (value) => (value === null || value === undefined || value === '' ? '—' : esc(value));
const when = (value) => (value ? esc(new Date(value).toLocaleString()) : '—');
const yesNo = (value) => (value ? 'Yes' : 'No');

const STYLE = `<style>
  .ar h3 { font-size: 13px; margin: 16px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #999; }
  .ar table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 11px; }
  .ar th, .ar td { border: 1px solid #999; padding: 4px 6px; text-align: left; vertical-align: top; }
  .ar th { background: #f0f0f0; font-weight: 600; }
  .ar .kv th { width: 28%; }
  .ar .outcome { border: 1.5px solid #333; padding: 8px 10px; margin: 8px 0 10px; font-size: 13px; font-weight: 700; }
  .ar .note { font-size: 10px; color: #444; margin-top: 6px; }
  .ar ul { margin: 0; padding-left: 16px; }
</style>`;

const keyValues = (pairs) => `<table class="kv">${pairs
  .filter(Boolean)
  .map(([label, value]) => `<tr><th>${esc(label)}</th><td>${value}</td></tr>`)
  .join('')}</table>`;

const signatureTable = (rows) => `<h3>Sign-off</h3><table>
  <tr><th>Stage</th><th>Name</th><th>Date and time</th><th>Decision / comment</th></tr>
  ${rows.map(([stage, name, at, comment]) => `<tr><td>${esc(stage)}</td><td>${text(name)}</td><td>${when(at)}</td><td>${text(comment)}</td></tr>`).join('')}
</table>`;

export const riskAssessmentReportHTML = (item) => {
  const factors = [
    ['Toxicity', item.toxicity_risk_score, [item.toxicity_basis, item.toxicity_notes].filter(Boolean).join(' — ')],
    ['Solubility', item.solubility_risk_score, [item.solubility_class, item.solubility_notes].filter(Boolean).join(' — ')],
    ['Cleanability', item.cleanability_risk_score, [item.cleanability_rating, item.cleanability_notes].filter(Boolean).join(' — ')],
    ['Patient population', item.patient_population_risk, item.patient_population],
    ['Batch size', item.batch_size_risk, item.batch_size ? `${item.batch_size} ${item.batch_size_unit || ''}` : ''],
    ['Equipment sharing', item.equipment_risk_score, [item.shared_equipment ? 'Shared equipment' : 'Dedicated equipment', item.equipment_notes].filter(Boolean).join(' — ')],
  ];
  const fmeaRows = Array.isArray(item.fmea_items) ? item.fmea_items : [];
  const evidence = (ref, files) => {
    const names = (Array.isArray(files) ? files : []).map((f) => f.originalname || f.filename);
    return [ref, names.length ? `Attached: ${names.join(', ')}` : ''].filter(Boolean).map(esc).join('<br>') || '—';
  };
  const hazards = [item.is_genotoxic && 'Genotoxic', item.is_carcinogenic && 'Carcinogenic', item.is_sensitizer && 'Sensitizer / allergen'].filter(Boolean);
  return `${STYLE}<div class="ar">
    <div class="outcome">Risk level: ${text(item.risk_level)} &nbsp;|&nbsp; Total score: ${text(item.total_risk_score)} &nbsp;|&nbsp; Status: ${text(item.status || 'Draft')}</div>
    <h3>Product and API</h3>
    ${keyValues([
      ['Product', `${text(item.product_name || item.linked_product_name)} (${text(item.product_code || item.linked_product_code)})`],
      ['API', `${text(item.api_name)}${item.api_code ? ` (${esc(item.api_code)})` : ''}`],
      ['PDE / ADE', item.ade_pde_value ? `${esc(item.ade_pde_value)} ${esc(item.ade_pde_unit || '')}` : '—'],
      ['LD50', item.ld50_value ? `${esc(item.ld50_value)} ${esc(item.ld50_unit || '')}` : '—'],
      ['Max daily dose', item.max_daily_dose ? `${esc(item.max_daily_dose)} ${esc(item.max_daily_dose_unit || '')}` : '—'],
      ['Hazard flags', hazards.length ? esc(hazards.join(', ')) : 'None'],
    ])}
    <h3>Risk ranking</h3>
    <table>
      <tr><th>Factor</th><th style="width:70px">Score</th><th>Basis</th></tr>
      ${factors.map(([name, score, basis]) => `<tr><td>${esc(name)}</td><td>${text(score)}</td><td>${text(basis)}</td></tr>`).join('')}
      <tr><th>Total</th><th>${text(item.total_risk_score)}</th><th>${text(item.risk_level)}</th></tr>
    </table>
    <h3>Risk control</h3>
    ${keyValues([
      ['Current controls', text(item.current_controls)],
      ['Proposed controls / actions', text(item.proposed_controls)],
      ['Evidence of proposed action', evidence(item.control_evidence_ref, item.control_evidence_files)],
    ])}
    <h3>FMEA worksheet</h3>
    ${fmeaRows.length === 0 ? '<p>No failure modes recorded.</p>' : `<table>
      <tr><th>#</th><th>Risk factor</th><th>Failure mode</th><th>Effect</th><th>Cause</th><th>Current controls</th><th>S</th><th>O</th><th>D</th><th>RPN</th><th>Proposed control / action</th><th>Responsible</th><th>Target date</th><th>Status</th><th>Evidence</th><th>Rev. S/O/D</th><th>Residual RPN</th></tr>
      ${fmeaRows.map((r, i) => `<tr>
        <td>${i + 1}</td><td>${text(r.risk_factor)}</td><td>${text(r.failure_mode)}</td><td>${text(r.effect)}</td><td>${text(r.cause)}</td>
        <td>${text(r.current_controls)}</td><td>${text(r.severity)}</td><td>${text(r.occurrence)}</td><td>${text(r.detectability)}</td><td><strong>${text(r.rpn)}</strong></td>
        <td>${text(r.proposed_controls)}</td><td>${text(r.responsible)}</td><td>${text(r.target_date)}</td><td>${text(r.action_status)}</td>
        <td>${evidence(r.evidence_ref, r.evidence_files)}</td>
        <td>${r.revised_severity ? `${esc(r.revised_severity)}/${esc(r.revised_occurrence ?? '—')}/${esc(r.revised_detectability ?? '—')}` : '—'}</td><td>${text(r.residual_rpn)}</td>
      </tr>`).join('')}
    </table>`}
    <h3>Conclusion</h3>
    ${keyValues([
      ['Validation approach', text(item.validation_approach)],
      ['Cleaning strategy', text(item.cleaning_strategy)],
      ['Special precautions', text(item.special_precautions)],
      ['Regulatory reference', text(item.regulatory_reference)],
      ['Additional notes', text(item.additional_notes)],
    ])}
    ${signatureTable([
      ['Prepared / submitted', item.saved_by_name, item.saved_at, item.save_comment],
      ['Production approval', item.product_approved_by_name, item.product_approved_at, [item.product_approval_decision, item.product_approval_comment].filter(Boolean).join(' — ')],
      ['QA approval', item.qa_approved_by_name, item.qa_approved_at, [item.qa_approval_decision, item.qa_approval_comment].filter(Boolean).join(' — ')],
    ])}
  </div>`;
};

export const impactAssessmentReportHTML = (record) => {
  const trains = Array.isArray(record.train_results) ? record.train_results : [];
  return `${STYLE}<div class="ar">
    <div class="outcome">Cleaning validation ${record.cv_required ? 'REQUIRED' : 'NOT REQUIRED'}${record.manual_override ? ' (manual override)' : ''} &nbsp;|&nbsp; Status: ${text(record.status)}</div>
    <h3>Product</h3>
    ${keyValues([
      ['Product', `${text(record.product_name)} (${text(record.product_code)})`],
      ['System screening result', `Cleaning validation ${record.system_cv_required ? 'required' : 'not required'}`],
      ['System reason', text(record.system_reason)],
      record.manual_override ? ['Override reason', text(record.override_reason)] : null,
    ])}
    <h3>Findings by equipment train</h3>
    ${trains.length === 0 ? '<p>No equipment train linked.</p>' : `<table>
      <tr><th>Train</th><th>Candidate API</th><th>Becomes worst case</th><th>Batch size above current maximum</th><th>MACO basis likely affected</th><th>Equipment without a validated cleaning method</th></tr>
      ${trains.map((t) => `<tr>
        <td>${text(t.train_name)}${t.first_product_on_train ? '<br><em>First product on this train</em>' : ''}</td>
        <td>${text(t.candidate_api)}</td>
        <td>${yesNo(t.becomes_worst_case)}${t.previous_worst_case_api ? `<br>Current: ${esc(t.previous_worst_case_api)}` : ''}</td>
        <td>${yesNo(t.batch_increased)}${t.batch_increased ? `<br>${text(t.candidate_batch_size)} vs ${text(t.max_existing_batch_size)}` : ''}</td>
        <td>${yesNo(t.maco_likely_affected)}</td>
        <td>${(t.uncovered_equipment || []).length ? esc(t.uncovered_equipment.map((e) => e.equipment_name || e.equipment_code).join(', ')) : 'None'}</td>
      </tr>`).join('')}
    </table>`}
    <h3>Documentation</h3>
    ${keyValues([
      ['Determination method', text(record.contamination_method)],
      ['Cross-contamination risk', text(record.cross_contamination_note)],
      ['Justification', text(record.justification)],
    ])}
    ${signatureTable([
      ['Recorded', record.created_by_user, record.created_at, ''],
      ['QA decision', record.approved_by_user, record.approved_at, [record.approval_decision, record.approval_comment].filter(Boolean).join(' — ')],
    ])}
    <p class="note">This is a screening assessment. It does not replace the Worst Case ranking or the MACO calculation.</p>
  </div>`;
};

export const gapAssessmentReportHTML = (rows, filterLabel = '') => {
  const covered = rows.filter((row) => row.gaps.length === 0).length;
  return `${STYLE}<div class="ar">
    <div class="outcome">Trains: ${rows.length} &nbsp;|&nbsp; Plan coverage complete: ${covered} &nbsp;|&nbsp; With gaps: ${rows.length - covered}${filterLabel ? ` &nbsp;|&nbsp; Filter: ${esc(filterLabel)}` : ''}</div>
    <h3>Train coverage register</h3>
    <table>
      <tr><th>Train</th><th>Equipment</th><th>Worst case</th><th>CV sampling plan</th><th>Gaps</th><th>Manual contamination</th><th>Status</th></tr>
      ${rows.map((row) => `<tr>
        <td><strong>${text(row.train.train_name || row.train.train_code)}</strong><br>${text(row.train.train_code)}</td>
        <td>${row.members.length} item(s)${row.missingEquipment.length ? `<br>Missing locations: ${esc(row.missingEquipment.map((m) => m.equipment_name || m.equipment_code).join(', '))}` : ''}</td>
        <td>${row.worstCase && !row.worstCase.is_provisional ? `${text(row.worstCase.product_name)}<br>API: ${text(row.worstCase.api_name)}` : 'No approved worst case'}</td>
        <td>${row.worstCaseCvPlans.length ? row.worstCaseCvPlans.map((p) => `${esc(p.plan_code)} (Approved)`).join('<br>') : row.allCvPlans.length ? row.allCvPlans.map((p) => `${esc(p.plan_code)} (${esc(p.status || 'Draft')})`).join('<br>') : 'No CV plan'}</td>
        <td>${row.gaps.length ? `<ul>${row.gaps.map((gap) => `<li>${esc(gap)}</li>`).join('')}</ul>` : 'None'}</td>
        <td>${row.soilingForTrain.length ? `${row.soilingForTrain.length} logged` : 'None'}</td>
        <td>${row.gaps.length ? 'Gap' : 'Plan coverage complete'}</td>
      </tr>`).join('')}
    </table>
    <p class="note">"Plan coverage complete" means an approved plan matches the train's worst-case API and has a sample location for every linked equipment item and part. It does not mean the validation batches and QA results are complete. Manual contamination is tracked separately and does not close a gap.</p>
    ${signatureTable([['Reviewed by', '', '', ''], ['Approved by (QA)', '', '', '']])}
  </div>`;
};

// `build` may be async (it can fetch the records first). The window is opened before
// the first await so the browser treats it as a response to the click, not a pop-up.
export const printAssessmentReport = async ({ title, documentName, appName = 'PrimeClean', build, reportKey }) => {
  const printWindow = window.open('', '_blank', 'width=1200,height=900');
  if (!printWindow) {
    toast.error('Print window failed to open. Please check popup blocker.');
    return;
  }
  printWindow.document.write('<p style="font-family:sans-serif;padding:24px">Preparing report…</p>');
  try {
    const [contentHTML, settingsRes] = await Promise.all([
      Promise.resolve().then(build),
      settingsAPI.getAll().catch(() => null),
    ]);
    if (!contentHTML) throw new Error('Nothing to print for this record');
    const settings = settingsRes?.data?.data || {};
    setReportSettingsCache(settings);
    printWindow.document.open();
    printWindow.document.write(generatePrintHTML({
      contentHTML,
      reportKey,
      systemName: settings.app_name || appName,
      title,
      documentName,
      clientName: settings.report_client_name || 'Client Name',
      clientAddress: unitReportAddress(settings) || 'Client Address',
      clientLogoUrl: settings.report_client_logo || '',
      primeCleanLogoUrl: settings.report_primeclean_logo || '',
      revisionNo: settings.report_document_rev_no || '01',
      unitName: getActivePrintUnitName(),
    }));
    printWindow.document.close();
    setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);
  } catch (error) {
    printWindow.close();
    toast.error(error?.response?.data?.message || error.message || 'Could not prepare the report');
  }
};
